import { Box, Divider, Link } from '@chakra-ui/react';

export const WhyMMMMustBecomeDefaultContent = () => (
  <Box>
    {/* Introduction */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="20px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        fontFamily="marketingPage">
        A C-Suite Guide to Confident, Evidence-Led Growth
      </Box>
      <Box mb={4}>
        Commercial leaders today are operating in conditions defined by
        uncertainty: fragmented media, volatile demand, rising acquisition
        costs, and intense scrutiny on profitability. The traditional
        measurement methods that once guided investment decisions can no longer
        explain enterprise-level performance or answer the questions boards now
        expect leaders to quantify.
      </Box>
      <Box mb={4}>
        Marketing Mix Modeling (MMM) is re-emerging as the commercial language
        because it does something no other approach can: it gives organizations
        a unified, financially grounded view of what truly drives growth.
      </Box>
      <Box mb={4}>
        The IPA’s whitepaper Econometrics and the C-suite shows a clear shift.
        MMM has evolved from a specialist analytics tool into a core strategic
        capability that aligns marketing, finance, and commercial operations
        around objective evidence rather than subjective impressions and debate.
      </Box>
      <Box mb={4}>
        Merkatus.io sees this shift accelerating. The organizations that embed
        MMM at the center of decision making are moving faster, reallocating
        capital more confidently, and reducing risk in ways that traditional
        measurement cannot match.
      </Box>
    </Box>
    <Divider my={12} />

    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        1. A Shared Financial Truth for the C-Suite
      </Box>
      <Box mb={4}>
        Senior leaders need one coherent view that connects media, pricing,
        promotions, distribution, and product mix to commercial outcomes. MMM
        provides this unifying lens by quantifying the true incremental lift of
        each lever. The result is clarity. Debates that were once based on
        opinion become grounded in financial impact.
      </Box>

      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        2. Capital Allocation with Confidence
      </Box>
      <Box mb={4}>
        In a world of constrained budgets, leadership teams cannot rely on
        intuition to decide where the next Euro delivers the highest return. MMM
        clarifies where spend becomes unprofitable, which tactics drive
        sustainable uplift, how pricing affects margin, and how brand investment
        compounds over time. Better visibility means better capital deployment.
        Fewer blind spots. More intelligent trade-offs.
      </Box>

      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        3. Scenario Planning That Reduces Risk{' '}
      </Box>
      <Box mb={4}>
        MMM allows leaders to test decisions before committing resources. The
        IPA whitepaper highlights its value for exploring alternative spend
        levels, pricing strategies, distribution changes, and competitive
        pressures. This positions MMM not as a reporting function but as a
        strategic forecasting engine that shapes planning cycles, not just
        retroactive measurement.
      </Box>

      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        4. Measurement That Reflects Modern Reality{' '}
      </Box>
      <Box mb={4}>
        Attribution no longer keeps pace with privacy changes and fragmented
        journeys, however MMM does. It connects activity to commercial outcomes
        by capturing both short-term activation effects and long-term brand
        value, which often drives more than half of total impact. For leaders
        accountable for sustainable growth, this complete lens is indispensable.
      </Box>

      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        5. Faster, Smarter, and More Accessible{' '}
      </Box>
      <Box mb={4}>
        Advances in automation and AI are reshaping expectations for MMM.
        Insights that once arrived annually can now be refreshed continuously.
        Merkatus.io views this as the next major unlock: MMM that operates at
        the speed of decision making, not the speed of reporting. When
        leadership teams can revisit scenarios weekly or monthly, evidence
        becomes integrated into the operating rhythm of the business.
      </Box>
    </Box>
    <Divider my={12} />

    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        The Leadership Imperative{' '}
      </Box>
      <Box mb={4}>
        MMM is becoming the commercial operating system that modern
        organizations require. It clarifies trade-offs, aligns functions, and
        elevates marketing from a cost center to a value-generating engine.{' '}
        <br />
        In a landscape where accountability and profitable growth matter more
        than ever, leaders need clarity they can trust. MMM provides the
        evidence base required to make the right calls.
      </Box>
      <Box mb={4}>
        Merkatus.io is committed to helping organizations build this capability
        and embed evidence-led planning at the heart of commercial decision
        making. The companies that adopt MMM as their default language will
        shape the next era of growth.
      </Box>
    </Box>
    <Divider my={12} />

    {/* CTA Section */}
    <Box mt={8}>
      <Box mb={6} color="text.darkBlue.800" fontSize="18px">
        Source{' '}
        <Link
          href="https://ipa.co.uk/knowledge/publications-reports/econometrics-and-the-c-suite"
          color="icon.green"
          textDecoration="underline"
          _hover={{ textDecoration: 'none' }}
          isExternal>
          https://ipa.co.uk/knowledge/publications-reports/econometrics-and-the-c-suite
        </Link>
      </Box>
    </Box>
  </Box>
);
